import React, { useState, useRef, useEffect, useMemo } from 'react';
import styles from '../../Assets/Styles/CountrySelector.module.css';
import mapImage from '../../Assets/Images/globe.png'; // Your map
import { COUNTRIES } from './DemoConstants'; // Import countries from constants

// Outer container size
const SVG_WIDTH = 320;
const SVG_HEIGHT = 380;

// Define how big you want the map itself to be inside the container
// (Pick values smaller than the container so there's a margin around it)
const MAP_IMAGE_WIDTH = 320;
const MAP_IMAGE_HEIGHT = 260;

// Calculate offsets so the map is centered in the container
const MAP_X_OFFSET = (SVG_WIDTH - MAP_IMAGE_WIDTH) / 2;
const MAP_Y_OFFSET = (SVG_HEIGHT - MAP_IMAGE_HEIGHT) / 2;

// For the bounding circle, let's center it at the middle of the map image
// You can also remove this logic if you don't want a circular clamp
const CENTER_X = MAP_X_OFFSET + MAP_IMAGE_WIDTH / 2;
const CENTER_Y = MAP_Y_OFFSET + MAP_IMAGE_HEIGHT / 2;
const RADIUS = 120; // tweak as needed

const CountrySelector = ({ onCountrySelect, selectedCountry, availableCountries, useNewMethod, onCharacterChange }) => {
    const svgRef = useRef(null);
    const updateBlockerRef = useRef(false);
    const [isDragging, setIsDragging] = useState(false);
    const [markerPosition, setMarkerPosition] = useState({
        x: CENTER_X,
        y: CENTER_Y,
    });

    // Filter countries based on availability if availableCountries is provided
    const filteredCountries = useMemo(() => {
        if (useNewMethod) {
            // In v1 mode (new method), always show all countries
            return COUNTRIES;
        } else {
            // In v2 mode (old method), filter based on availability
            return availableCountries
                ? COUNTRIES.filter(country => availableCountries.includes(country.id))
                : COUNTRIES;
        }
    }, [availableCountries, useNewMethod]);

    // Convert mouse/touch event to SVG coordinates
    const getSVGCoordinates = (event) => {
        const svg = svgRef.current;
        if (!svg) return { x: 0, y: 0 };
        const rect = svg.getBoundingClientRect();

        // Touch
        if (event.touches && event.touches[0]) {
            return {
                x: event.touches[0].clientX - rect.left,
                y: event.touches[0].clientY - rect.top,
            };
        }
        // Mouse
        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
        };
    };

    // For the bounding circle approach
    const getDistanceFromCenter = (x, y) =>
        Math.sqrt((x - CENTER_X) ** 2 + (y - CENTER_Y) ** 2);

    // Clamps to the bounding circle if you want to prevent the marker from going too far
    const clampToCircle = (x, y) => {
        const distance = getDistanceFromCenter(x, y);
        if (distance <= RADIUS) {
            return { x, y };
        } else {
            const angle = Math.atan2(y - CENTER_Y, x - CENTER_X);
            const newX = CENTER_X + RADIUS * Math.cos(angle);
            const newY = CENTER_Y + RADIUS * Math.sin(angle);
            return { x: newX, y: newY };
        }
    };

    const handleMouseDown = (event) => {
        // Only disable if we're in v1 mode AND there are no available countries
        if (useNewMethod && availableCountries && availableCountries.length === 0) return;

        event.preventDefault();
        setIsDragging(true);
        const coords = getSVGCoordinates(event);
        setMarkerPosition(clampToCircle(coords.x, coords.y));
    };

    const handleMouseMove = (event) => {
        if (!isDragging) return;
        const coords = getSVGCoordinates(event);
        setMarkerPosition(clampToCircle(coords.x, coords.y));
    };

    const finalizeSelection = (x, y) => {
        let closest = null;
        let closestDistance = Infinity;

        // Find the closest country from the filtered list
        filteredCountries.forEach((c) => {
            const dist = Math.sqrt((c.position.x - x) ** 2 + (c.position.y - y) ** 2);
            if (dist < closestDistance) {
                closestDistance = dist;
                closest = c;
            }
        });

        if (closest) {
            setMarkerPosition({ ...closest.position });

            // Update country selection
            onCountrySelect(closest.id);

            // In v1 mode, also update the character to match the country
            if (useNewMethod && onCharacterChange) {
                // Find a character that supports this country/accent
                onCharacterChange(closest.id);
            }
        }
    };

    const handleMouseUp = () => {
        if (!isDragging) return;
        setIsDragging(false);
        finalizeSelection(markerPosition.x, markerPosition.y);
    };

    const handleMouseLeave = () => {
        if (isDragging) {
            setIsDragging(false);
            finalizeSelection(markerPosition.x, markerPosition.y);
        }
    };

    const handleTouchEnd = () => {
        if (!isDragging) return;
        setIsDragging(false);
        finalizeSelection(markerPosition.x, markerPosition.y);
    };

    // // If parent changes selectedCountry, snap marker to it
    // useEffect(() => {
    //     const found = filteredCountries.find((c) => c.id === selectedCountry);
    //     if (found) {
    //         setMarkerPosition({ ...found.position });
    //     } else if (filteredCountries.length > 0 && selectedCountry) {
    //         // If the selected country is not available, select the first available one
    //         setMarkerPosition({ ...filteredCountries[0].position });
    //         onCountrySelect(filteredCountries[0].id);
    //     }
    // }, [selectedCountry, filteredCountries, onCountrySelect]);

    // // If availableCountries changes and the current selectedCountry is not in it,
    // // select the first available one
    // useEffect(() => {
    //     // If we're already in the middle of an update cycle, don't continue
    //     if (updateBlockerRef.current) return;

    //     const found = filteredCountries.find((c) => c.id === selectedCountry);
    //     if (found) {
    //         setMarkerPosition({ ...found.position });
    //     } else if (filteredCountries.length > 0 && selectedCountry) {
    //         // Block any further updates before we make changes
    //         updateBlockerRef.current = true;

    //         // If the selected country is not available, select the first available one
    //         setMarkerPosition({ ...filteredCountries[0].position });

    //         // Call with a setTimeout to break the update cycle
    //         setTimeout(() => {
    //             onCountrySelect(filteredCountries[0].id);
    //             // Reset the blocker after this render cycle
    //             updateBlockerRef.current = false;
    //         }, 0);
    //     }

    //     // Handle the case when availableCountries changes
    //     if (availableCountries && availableCountries.length > 0 && !updateBlockerRef.current) {
    //         const isCurrentlySelectedAvailable = availableCountries.includes(selectedCountry);
    //         if (!isCurrentlySelectedAvailable && filteredCountries.length > 0) {
    //             // Block any further updates before we make changes
    //             updateBlockerRef.current = true;

    //             const firstAvailableCountry = filteredCountries[0];
    //             setMarkerPosition({ ...firstAvailableCountry.position });

    //             // Call with a setTimeout to break the update cycle
    //             setTimeout(() => {
    //                 onCountrySelect(firstAvailableCountry.id);
    //                 // Reset the blocker after this render cycle
    //                 updateBlockerRef.current = false;
    //             }, 0);
    //         }
    //     }
    // }, [selectedCountry, filteredCountries, availableCountries]);

    useEffect(() => {
        if (updateBlockerRef.current) return;

        // When switching modes, ensure marker has a valid position
        const found = filteredCountries.find((c) => c.id === selectedCountry);
        if (found) {
            setMarkerPosition({ ...found.position });
        } else if (filteredCountries.length > 0) {
            // If no country is selected or current selection is not in filtered list,
            // select the first available country
            setMarkerPosition({ ...filteredCountries[0].position });

            // Only update selection if it's actually different
            if (selectedCountry !== filteredCountries[0].id) {
                updateBlockerRef.current = true;
                setTimeout(() => {
                    onCountrySelect(filteredCountries[0].id);
                    updateBlockerRef.current = false;
                }, 0);
            }
        }
    }, [selectedCountry, filteredCountries, useNewMethod, onCountrySelect]);

    return (
        <div className={styles['country-wheel']}>
            <svg
                ref={svgRef}
                width={SVG_WIDTH}
                height={SVG_HEIGHT}
                viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                style={{
                    cursor: isDragging ? 'grabbing' : 'grab',
                    opacity: availableCountries && availableCountries.length === 0 ? 0.5 : 1
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleMouseDown}
                onTouchMove={handleMouseMove}
                onTouchEnd={handleTouchEnd}
            >
                {/* The map image */}
                <image
                    x={MAP_X_OFFSET}
                    y={MAP_Y_OFFSET}
                    width={MAP_IMAGE_WIDTH}
                    height={MAP_IMAGE_HEIGHT}
                    xlinkHref={mapImage}
                    preserveAspectRatio="xMidYMid meet"
                    style={{ filter: "grayscale(90%) blur(2px)" }}
                />

                {/* Optional crosshair lines */}
                {/* vertical guideline – full image height */}
                <line
                    x1={CENTER_X}
                    y1={CENTER_Y - RADIUS + 15}
                    x2={CENTER_X}
                    y2={CENTER_Y + RADIUS - 15}
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="1"
                />

                {/* horizontal guideline – full image width */}
                <line
                    x1={MAP_X_OFFSET}                       // left edge of map
                    y1={CENTER_Y}
                    x2={MAP_X_OFFSET + MAP_IMAGE_WIDTH}     // right edge of map
                    y2={CENTER_Y}
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="1"
                />


                {/* Render visible country positions */}
                {/* {filteredCountries.map((country) => (
                    <circle
                        key={country.id}
                        cx={country.position.x}
                        cy={country.position.y}
                        r={4}
                        fill="rgba(255, 255, 255, 0.5)"
                        stroke="none"
                    />
                ))} */}

                {/* The marker */}
                <circle
                    cx={markerPosition.x}
                    cy={markerPosition.y}
                    r="7"
                    fill="#fde4cd"
                    stroke="#313130"
                    strokeWidth="2"
                />
            </svg>

            {/* Display selected country */}
            <div style={{ color: 'white', fontSize: 'var(--font-size-normal)', fontWeight: 'bold', marginTop: '5px', textAlign: 'center' }}>
                {selectedCountry ?
                    (filteredCountries.find(c => c.id === selectedCountry)?.name || 'Unknown Country')
                    : 'No country selected'}
            </div>
        </div>
    );
};

export default CountrySelector;